import {
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';
import { Spinner, Text } from '@revelio/ui';

import { SyncInitialPreferencesToFilters } from '../../shared/user-preferences/SyncInitialPreferencesToFilters';
import type { RevelioPageId } from '../CustomDash/components/PageConfigProvider/types';
import { GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT } from '../CustomDash/constants/panelWidths';
import { useApplyAiSearchFilters } from '../CustomDash/filters/ai-search/useApplyAiSearchFilters';
import { FilterProvider } from '../CustomDash/filters/contexts/FilterProvider';
import type { PrimaryFilterId } from '../CustomDash/filters/types/filters';
import { nameableSelectionNames } from '../CustomDash/filters/utils/nameable-selection-names';
import styles from './global-ai-assistant-chart-filter-scope.module.css';
import type { GlobalAiChartPreviewRuntimeSnapshot } from './global-ai-assistant-chart-preview-runtime';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';

function ApplyAiSearchFiltersOnce({
  messageId,
  filters,
  primarySelectionMergeMode,
  loadingMinHeight = GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT,
  compactLoading = false,
  onApplyResult,
  children,
}: {
  messageId: string;
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  primarySelectionMergeMode: 'merge' | 'replace';
  loadingMinHeight?: string;
  compactLoading?: boolean;
  /**
   * Headless mode: report the apply outcome here INSTEAD of the GA
   * chart-preview runtime (which is keyed by transcript message ids the
   * headless digest host doesn't have).
   */
  onApplyResult?: (result: { ok: boolean; error?: string }) => void;
  children: ReactNode;
}) {
  const ga = useGlobalAIAssistantOptional();
  const mergeRuntimeRef = useRef<
    | ((
        id: string,
        patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
      ) => void)
    | undefined
  >(undefined);
  mergeRuntimeRef.current = ga?.mergeChartPreviewRuntime;
  const addTimelineEventRef = useRef<
    ((messageId: string, event: string) => void) | undefined
  >(undefined);
  addTimelineEventRef.current = ga?.addChartPreviewTimelineEvent;

  const { applyAiSearchFilters } = useApplyAiSearchFilters();
  const [filtersReady, setFiltersReady] = useState(filters === undefined);
  const applyRef = useRef(applyAiSearchFilters);
  applyRef.current = applyAiSearchFilters;
  const onApplyResultRef = useRef(onApplyResult);
  onApplyResultRef.current = onApplyResult;

  const filtersKey =
    filters === undefined ? '__none__' : JSON.stringify(filters);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useLayoutEffect(() => {
    const filtersNow = filtersRef.current;
    const merge = mergeRuntimeRef.current;
    // Headless mode: the outcome callback replaces the message-id-keyed
    // runtime reporting wholesale (there is no transcript message).
    const reportResult = onApplyResultRef.current;
    if (filtersNow === undefined) {
      if (reportResult) {
        reportResult({ ok: true });
      } else {
        merge?.(messageId, {
          phase: 'filters_ready',
          filterApplyError: undefined,
        });
        addTimelineEventRef.current?.(messageId, 'filters skipped (none)');
      }
      setFiltersReady(true);
      return;
    }

    if (!reportResult) {
      merge?.(messageId, {
        phase: 'applying_filters',
        filterApplyError: undefined,
      });
      addTimelineEventRef.current?.(messageId, 'applying filters');
    }
    setFiltersReady(false);

    let cancelled = false;
    void (async () => {
      try {
        const applyResult = await applyRef.current(filtersNow, {
          primarySelectionMergeMode,
          skipTalentDiscoveryAdaptFallback: true,
          source: 'global_assistant',
          // Headless data fetch: role, skill and activity ids MUST be queried
          // under the taxonomy they were resolved with, or the backend
          // rejects/mismatches them. Pairing is default-on for real page
          // applies too now (see the `applyRouteFilters` command docblock),
          // but each side there gates on an actual selection — this scope can
          // be a BREAKDOWN (groups BY role, skill or activity, e.g. top-N
          // families) with no instance to gate on, and it skips the taxonomy
          // preference sync below, so it opts out of those gates explicitly.
          pairRoleTaxonomyWithResponse: true,
          pairRoleTaxonomyEvenWithoutSelections: true,
          // This scope states the restriction itself (below), so the hook's
          // toast would duplicate it.
          reportsBlockedCompaniesToCaller: true,
        });
        // Companies outside the user's trial were stripped, so the remaining
        // scope is the plan's DEFAULT companies — answering from it would pass
        // off a figure for the wrong company as the answer. Fail with the
        // reason (same treatment as a failed apply) so the model can state it.
        const blocked = applyResult?.blockedCompanyNames ?? [];
        if (!cancelled && blocked.length > 0) {
          // Two audiences. The MODEL gets a reason carrying narration
          // instructions (it becomes `ChartDataDigest.errorReason`). The USER
          // gets nothing from here: publishing the blocked names lets the
          // portal render the shared `TrialCompanyRestrictionMessage`, so the
          // chart says exactly what the toast says, upgrade link included,
          // with no second copy of the wording to drift.
          const nameable = nameableSelectionNames(blocked);
          const subject =
            nameable.length > 0 ? nameable.join(', ') : 'That company';
          const isPlural = nameable.length > 1;
          const modelReason = `${subject} ${isPlural ? 'are' : 'is'} not included in this user's trial plan, so ${isPlural ? 'they were' : 'it was'} removed from the filters. This is a plan limit, not a temporary failure — retrying will not help. Tell the user the company is not available on their trial and that upgrading unlocks it; do not state any figure for it.`;
          if (onApplyResultRef.current) {
            onApplyResultRef.current({ ok: false, error: modelReason });
          } else {
            mergeRuntimeRef.current?.(messageId, {
              phase: 'error',
              filterApplyBlockedCompanies: blocked,
            });
            addTimelineEventRef.current?.(messageId, 'company not on plan');
          }
          return;
        }
        if (!cancelled) {
          if (onApplyResultRef.current) {
            onApplyResultRef.current({ ok: true });
          } else {
            mergeRuntimeRef.current?.(messageId, {
              phase: 'filters_ready',
              filterApplyError: undefined,
            });
            addTimelineEventRef.current?.(messageId, 'ai filters applied');
          }
        }
      } catch (e) {
        const msg =
          e instanceof Error ? e.message : 'Failed to apply AI filters';
        if (!cancelled) {
          if (onApplyResultRef.current) {
            onApplyResultRef.current({ ok: false, error: msg });
          } else {
            mergeRuntimeRef.current?.(messageId, {
              phase: 'error',
              filterApplyError: msg,
            });
            addTimelineEventRef.current?.(
              messageId,
              `ai filters failed: ${msg}`
            );
          }
        }
      } finally {
        if (!cancelled) {
          setFiltersReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filtersKey, messageId, primarySelectionMergeMode]);

  if (!filtersReady) {
    if (compactLoading) {
      return (
        <div className={styles.loadingState}>
          <Spinner size="sm" />
          <Text fontSize="x-sm" className={styles.loadingLabel}>
            Applying filters…
          </Text>
        </div>
      );
    }
    return (
      <div
        className={styles.loadingCenter}
        style={{ '--loading-min-height': loadingMinHeight } as CSSProperties}
      >
        <Spinner />
      </div>
    );
  }

  return children;
}

/**
 * Isolated filter state for a single global-assistant chart preview.
 * Does not read or write URL filters; applies the AI search snapshot once per message.
 */
export function GlobalAIAssistantChartFilterScope({
  messageId,
  filters,
  revelioPageId,
  primaryFilterId,
  primarySelectionMergeMode = 'replace',
  loadingMinHeight = GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT,
  compactLoading = false,
  onApplyResult,
  children,
}: {
  messageId: string;
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  revelioPageId?: RevelioPageId;
  primaryFilterId?: PrimaryFilterId;
  /**
   * `replace`: do not union AI primary entities with template defaults (single-bar previews).
   * @default 'replace'
   */
  primarySelectionMergeMode?: 'merge' | 'replace';
  loadingMinHeight?: string;
  compactLoading?: boolean;
  /**
   * Headless mode (digest host): receive the AI filter-apply outcome instead
   * of reporting it to the chart-preview runtime. See ApplyAiSearchFiltersOnce.
   */
  onApplyResult?: (result: { ok: boolean; error?: string }) => void;
  children: ReactNode;
}) {
  return (
    <FilterProvider
      key={messageId}
      revelioPageId={revelioPageId}
      primaryFilterId={primaryFilterId}
      hydrateFromUrl={false}
      syncFiltersToUrl={false}
      persistScope={false}
    >
      {/* skipTaxonomies: this scope's taxonomy pairing comes from the AI response;
          pref-sync's first-mount passive effect reads pre-apply filter state, so
          its isAiApplied guard can't see the apply and would clobber the pairing. */}
      <SyncInitialPreferencesToFilters
        revelioPageId={revelioPageId}
        skipTaxonomies
      />
      <ApplyAiSearchFiltersOnce
        messageId={messageId}
        filters={filters}
        primarySelectionMergeMode={primarySelectionMergeMode}
        loadingMinHeight={loadingMinHeight}
        compactLoading={compactLoading}
        onApplyResult={onApplyResult}
      >
        {children}
      </ApplyAiSearchFiltersOnce>
    </FilterProvider>
  );
}
